import { QueryClient, QueryClientProvider, useQuery, useQueryClient } from "@tanstack/react-query";
import { Suspense, lazy, useEffect, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router";
import { Toaster } from "sonner";
import { AppShell } from "@/components/layout/AppShell";
import { StepUpDialog } from "@/components/StepUpDialog";
import { Spinner } from "@/components/ui/core";
import { ConfirmHost, TooltipProvider } from "@/components/ui/overlay";
import { ApiError, api, onUnauthorized, setCsrf } from "@/lib/api";
import { useMediaQuery, useTheme } from "@/lib/hooks";
import { startLive, stopLive } from "@/lib/live";
import { LoginPage } from "@/pages/Login";

const Dashboard = lazy(() => import("@/pages/Dashboard"));
const MarketWatch = lazy(() => import("@/pages/MarketWatch"));
const ChartsPage = lazy(() => import("@/pages/Charts"));
const Strategies = lazy(() => import("@/pages/Strategies"));
const StrategyEditor = lazy(() => import("@/pages/StrategyEditor"));
const Backtests = lazy(() => import("@/pages/Backtests"));
const BacktestDetail = lazy(() => import("@/pages/BacktestDetail"));
const Trades = lazy(() => import("@/pages/Trades"));
const OptionChain = lazy(() => import("@/pages/OptionChain"));
const Markets = lazy(() => import("@/pages/Markets"));
const Risk = lazy(() => import("@/pages/Risk"));
const Journal = lazy(() => import("@/pages/Journal"));
const SettingsPage = lazy(() => import("@/pages/Settings"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (n, e) => !(e instanceof ApiError && [400, 401, 403, 404, 422].includes(e.status)) && n < 2,
      refetchOnWindowFocus: false,
    },
  },
});

interface AuthState {
  setup_required: boolean;
  user: { username: string; csrf: string; totp_enabled: boolean } | null;
}

function Gate() {
  const qc = useQueryClient();
  const auth = useQuery({ queryKey: ["auth"], queryFn: () => api.get<AuthState>("/api/auth/state"), staleTime: Infinity });
  const [signedOut, setSignedOut] = useState(false);
  const user = signedOut ? null : auth.data?.user;

  useEffect(() => onUnauthorized(() => setSignedOut(true)), []);
  // one live connection per session: keyed on the session's token, not on the user
  // object, which a refetch replaces (and which restarted the socket each login)
  const csrf = user?.csrf;
  useEffect(() => {
    if (csrf) {
      setCsrf(csrf);
      startLive();
      return () => stopLive();
    }
  }, [csrf]);

  if (auth.isLoading)
    return (
      <div className="grid h-full place-items-center">
        <Spinner className="size-5" />
      </div>
    );
  if (auth.isError)
    return (
      <div className="grid h-full place-items-center px-6 text-center text-[13px] text-muted">
        The server is not reachable. Start it with <span className="mono">algotrader dev</span> and reload.
      </div>
    );
  if (!user)
    return (
      <LoginPage
        setupRequired={!!auth.data?.setup_required}
        onSetupDone={() => qc.setQueryData(["auth"], { setup_required: false, user: null })}
        onSignedIn={(u) => {
          setCsrf(u.csrf);
          setSignedOut(false);
          qc.setQueryData(["auth"], { setup_required: false, user: { ...u, totp_enabled: false } });
          qc.invalidateQueries({ queryKey: ["auth"] });
        }}
      />
    );

  const signOut = async () => {
    try {
      await api.post("/api/auth/logout");
    } finally {
      stopLive();
      qc.clear();
      setSignedOut(true);
    }
  };

  return (
    <BrowserRouter>
      <AppShell user={user.username} onSignOut={signOut}>
        <Suspense
          fallback={
            <div className="grid h-60 place-items-center">
              <Spinner />
            </div>
          }
        >
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/watch" element={<MarketWatch />} />
            <Route path="/charts" element={<ChartsPage />} />
            <Route path="/strategies" element={<Strategies />} />
            <Route path="/strategies/new" element={<StrategyEditor />} />
            <Route path="/strategies/:id" element={<StrategyEditor />} />
            <Route path="/backtests" element={<Backtests />} />
            <Route path="/backtests/:id" element={<BacktestDetail />} />
            <Route path="/trades" element={<Trades />} />
            <Route path="/options" element={<OptionChain />} />
            <Route path="/markets" element={<Markets />} />
            <Route path="/risk" element={<Risk />} />
            <Route path="/journal" element={<Journal />} />
            <Route path="/settings/*" element={<SettingsPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </AppShell>
      <StepUpDialog totp={user.totp_enabled} />
      <ConfirmHost />
    </BrowserRouter>
  );
}

function ThemedToaster() {
  const [theme] = useTheme();
  // a phone shows toasts at the top, clear of the tab bar and the thumb
  const phone = useMediaQuery("(max-width: 767px)");
  return (
    <Toaster
      position={phone ? "top-center" : "bottom-right"}
      mobileOffset={{ top: "calc(env(safe-area-inset-top) + 8px)" }}
      theme={theme}
      richColors
      closeButton={!phone}
      toastOptions={{ className: "text-[13px]" }}
    />
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider delayDuration={250}>
        <Gate />
        <ThemedToaster />
      </TooltipProvider>
    </QueryClientProvider>
  );
}
